import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useTelemetryStore } from '../stores/telemetryStore';

export interface TelemetryData {
  received_at?: number;
  temp_c?: number;
  power_w?: number;
  temperature?: number;
  powerLoad?: number;
  powerCapacity?: number;
  hasSpof?: boolean;
}

interface StreamContext {
  es: EventSource | null;
  metrics: Record<string, TelemetryData>;
  subscribers: Set<React.Dispatch<React.SetStateAction<Record<string, TelemetryData>>>>;
  gcTimeout: number | NodeJS.Timeout | null;
  retryCount: number;
  reconnectTimeout: number | NodeJS.Timeout | null;
}

const streams = new Map<string, StreamContext>();

function connectStream(streamKey: string, dcCode?: string, rackId?: number) {
  const ctx = streams.get(streamKey);
  if (!ctx) return;

  if (ctx.es) {
    ctx.es.close();
    ctx.es = null;
  }
  if (ctx.reconnectTimeout) {
    clearTimeout(ctx.reconnectTimeout);
    ctx.reconnectTimeout = null;
  }

  if (!navigator.onLine) {
    useTelemetryStore.getState().setStatus('offline');
    return;
  }

  useTelemetryStore.getState().setStatus('connecting');

  const params = new URLSearchParams();
  if (dcCode) params.append('dc_code', dcCode);
  if (rackId) params.append('rack_id', rackId.toString());

  const url = `/api/telemetry/stream?${params.toString()}`;
  const es = new EventSource(url, { withCredentials: true });
  ctx.es = es;

  es.onopen = () => {
    useTelemetryStore.getState().setStatus('connected');
    ctx.retryCount = 0;
  };

  es.addEventListener('metrics_update', (e) => {
    try {
      const payload = JSON.parse(e.data);
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return;
      const received_at = Date.now();
      ctx.metrics = Object.fromEntries(Object.entries(payload).filter(([, value]) => value && typeof value === 'object').map(([id, value]) => {
        const reading = value as Record<string, unknown>;
        const fields = Object.fromEntries(['temp_c', 'power_w', 'temperature', 'powerLoad', 'powerCapacity'].filter(
          field => typeof reading[field] === 'number' && Number.isFinite(reading[field]) && (field === 'temp_c' || field === 'temperature' || Number(reading[field]) >= 0),
        ).map(field => [field, reading[field]]));
        return [id, { ...fields, received_at }];
      }));
      ctx.subscribers.forEach(sub => sub(ctx.metrics));
    } catch (err) {
      console.error("Failed to parse telemetry update", err);
    }
  });

  es.onerror = () => {
    es.close();
    ctx.es = null;

    if (!navigator.onLine) {
      useTelemetryStore.getState().setStatus('offline');
      return;
    }

    useTelemetryStore.getState().setStatus('connecting');
    ctx.retryCount += 1;
    // Exponential backoff with jitter (max 30s)
    const delay = Math.min(30000, 1000 * Math.pow(2, ctx.retryCount)) + Math.random() * 1000;
    ctx.reconnectTimeout = setTimeout(() => {
      connectStream(streamKey, dcCode, rackId);
    }, delay);
  };
}

export function useTelemetryStream(dcCode?: string, rackId?: number) {
  const [metrics, setMetrics] = useState<Record<string, TelemetryData>>({});
  const [now, setNow] = useState(Date.now);
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (!isAuthenticated || (!dcCode && !rackId)) return;

    const streamKey = `${dcCode || ''}-${rackId || ''}`;
    
    if (!streams.has(streamKey)) {
      streams.set(streamKey, {
        es: null,
        metrics: {},
        subscribers: new Set(),
        gcTimeout: null,
        retryCount: 0,
        reconnectTimeout: null
      });
    }
    const ctx = streams.get(streamKey)!;
    
    if (ctx.gcTimeout) {
      clearTimeout(ctx.gcTimeout);
      ctx.gcTimeout = null;
    }
    
    // Initial sync
    setMetrics({ ...ctx.metrics });
    ctx.subscribers.add(setMetrics);
    const freshnessTimer = setInterval(() => setNow(Date.now()), 5_000);

    if (!ctx.es && !ctx.reconnectTimeout) {
      connectStream(streamKey, dcCode, rackId);
    }

    // Resilience handlers for network and visibility change
    const handleVisibility = () => {
      if (document.hidden) {
        if (ctx.es) {
          ctx.es.close();
          ctx.es = null;
        }
        if (ctx.reconnectTimeout) {
          clearTimeout(ctx.reconnectTimeout);
          ctx.reconnectTimeout = null;
        }
        useTelemetryStore.getState().setStatus('disconnected');
      } else {
        ctx.retryCount = 0;
        connectStream(streamKey, dcCode, rackId);
      }
    };

    const handleOnline = () => {
      ctx.retryCount = 0;
      connectStream(streamKey, dcCode, rackId);
    };

    const handleOffline = () => {
      if (ctx.es) {
        ctx.es.close();
        ctx.es = null;
      }
      if (ctx.reconnectTimeout) {
        clearTimeout(ctx.reconnectTimeout);
        ctx.reconnectTimeout = null;
      }
      useTelemetryStore.getState().setStatus('offline');
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(freshnessTimer);
      ctx.subscribers.delete(setMetrics);
      
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (ctx.subscribers.size === 0) {
        ctx.gcTimeout = setTimeout(() => {
          if (ctx.subscribers.size === 0) {
            if (ctx.es) {
              ctx.es.close();
              ctx.es = null;
            }
            if (ctx.reconnectTimeout) {
              clearTimeout(ctx.reconnectTimeout);
              ctx.reconnectTimeout = null;
            }
            ctx.metrics = {}; // GC
            streams.delete(streamKey);
          }
        }, 3000);
      }
    };
  }, [dcCode, rackId, isAuthenticated]);

  // ponytail: receipt age only (30s), source measurement timestamps are not supplied yet.
  return useMemo(() => Object.fromEntries(Object.entries(metrics).filter(([, reading]) =>
    reading.received_at != null && Math.max(now, Date.now()) - reading.received_at <= 30_000,
  )), [metrics, now]);
}
